/**
 * Graph Demo - Sample Report Using Computation Graph
 *
 * A quick test page to verify the library works before migrating the real report.
 */

import React from 'react';
import {
  GraphProvider,
  V,
  InspectorPanel,
  OverrideBanner,
  useOverrides,
} from '../lib/computation-graph';
import { houseDecisionGraph } from '../graphs/house-decision';

/**
 * Reset button component
 */
function ResetButton() {
  const { hasOverrides, clearAllOverrides } = useOverrides();

  if (!hasOverrides) return null;

  return (
    <button
      onClick={clearAllOverrides}
      className="text-sm text-gray-500 hover:text-gray-700 underline"
    >
      Reset all to defaults
    </button>
  );
}

/**
 * Demo content with embedded values
 */
function DemoContent() {
  return (
    <div className="max-w-3xl">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">
        Computation Graph Demo
      </h1>

      <p className="text-gray-600 mb-8">
        Click any underlined number to inspect it. For assumptions (like property value),
        you can adjust them with sliders. Watch how dependent values update automatically.
      </p>

      {/* Table of Contents */}
      <nav className="mb-10 p-4 bg-gray-50 border border-gray-200">
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Contents</h2>
        <ul className="space-y-1 text-sm">
          <li><a href="#property-overview" className="text-gray-600 hover:text-gray-900">Property Overview</a></li>
          <li><a href="#rental-analysis" className="text-gray-600 hover:text-gray-900">Rental Analysis (Year 1)</a></li>
          <li><a href="#projections" className="text-gray-600 hover:text-gray-900">10-Year Projections</a></li>
          <li><a href="#returns" className="text-gray-600 hover:text-gray-900">Return Assumptions</a></li>
          <li><a href="#tax-constants" className="text-gray-600 hover:text-gray-900">Tax Constants (Sources)</a></li>
        </ul>
      </nav>

      {/* Property Overview */}
      <section id="property-overview" className="mb-8 scroll-mt-16">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Property Overview</h2>
        <p className="text-gray-700 mb-4">
          The property is valued at <V id="propertyValue" /> according to Zillow.
          At a selling cost of <V id="sellingCosts" format="percent" decimals={1} />,
          you would net <V id="initialSaleProceeds" /> if you sold today.
        </p>
        <p className="text-gray-700 mb-4">
          The building portion (for depreciation purposes) is worth <V id="buildingValue" />,
          which represents <V id="coastalBuildingRatio" format="percent" decimals={0} /> of
          the total value—typical for coastal California where land is expensive.
        </p>
      </section>

      {/* Rental Analysis */}
      <section id="rental-analysis" className="mb-8 scroll-mt-16">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Rental Analysis (Year 1)</h2>
        <p className="text-gray-700 mb-4">
          Monthly rent of <V id="monthlyRent" /> translates to <V id="annualRent.year0" /> per year.
          After accounting for <V id="vacancyRate" format="percent" decimals={0} /> vacancy,
          gross rental income is <V id="grossRentalIncome.year0" />.
        </p>

        <div className="bg-gray-50 border border-gray-200 p-4 mb-4">
          <h3 className="font-semibold text-gray-800 mb-3">Expense Breakdown</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Property Tax</span>
              <span><V id="propertyTax.year0" /></span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Insurance</span>
              <span><V id="insuranceCost" /></span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Maintenance</span>
              <span><V id="maintenance.year0" /></span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Management</span>
              <span><V id="managementCost.year0" /></span>
            </div>
            <div className="flex justify-between border-t border-gray-300 pt-2 font-medium">
              <span className="text-gray-800">Total Expenses</span>
              <span><V id="totalExpenses.year0" /></span>
            </div>
          </div>
        </div>

        <p className="text-gray-700 mb-4">
          Net rental income before tax: <V id="netRentalIncome.year0" />.
          After the <V id="depreciationDeduction.year0" /> depreciation deduction,
          taxable income is <V id="taxableRentalIncome.year0" />,
          resulting in <V id="rentalIncomeTax.year0" /> of rental income tax.
        </p>
        <p className="text-gray-700">
          <strong>After-tax net income: <V id="netRentalIncomeAfterTax.year0" /></strong>
        </p>
      </section>

      {/* Year 10 Projections */}
      <section id="projections" className="mb-8 scroll-mt-16">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">10-Year Projections</h2>

        <p className="text-gray-700 mb-4">
          At <V id="propertyAppreciation" format="percent" decimals={1} /> annual appreciation,
          the property would be worth <V id="propertyValue.year10" /> by year 10.
        </p>

        <div className="grid md:grid-cols-2 gap-4 mb-6">
          {/* Rent Scenario */}
          <div className="bg-gray-50 border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-800 mb-3">Keep & Rent</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Property Value</span>
                <span><V id="propertyValue.year10" /></span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">+ Invested Income</span>
                <span><V id="investedRentalPortfolio.year10" /></span>
              </div>
              <div className="flex justify-between text-gray-500">
                <span>− Setup Costs</span>
                <span><V id="rentalSetupCosts" /></span>
              </div>
              <div className="flex justify-between border-t border-gray-200 pt-2">
                <span className="text-gray-700">Total Equity</span>
                <span><V id="rentTotalEquity" /></span>
              </div>
              <div className="flex justify-between text-gray-500">
                <span>− Depreciation Recapture</span>
                <span><V id="depreciationRecaptureTax" /></span>
              </div>
              <div className="flex justify-between text-gray-500">
                <span>− Capital Gains Tax</span>
                <span><V id="rentCapitalGainsTax" /></span>
              </div>
              <div className="flex justify-between text-gray-500">
                <span>− Selling Costs</span>
                <span><V id="sellingCostsAtExit" /></span>
              </div>
              <div className="flex justify-between border-t border-gray-300 pt-2 font-bold">
                <span className="text-gray-900">Net Liquidation</span>
                <span><V id="rentNetLiquidation" /></span>
              </div>
            </div>
          </div>

          {/* Sell Scenario */}
          <div className="bg-gray-50 border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-800 mb-3">Sell & Invest (80/20)</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Initial Proceeds</span>
                <span><V id="initialSaleProceeds" /></span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Stock Return</span>
                <span><V id="effectiveStockReturn" format="percent" decimals={2} /></span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Bond Return</span>
                <span><V id="effectiveBondReturn" format="percent" decimals={2} /></span>
              </div>
              <div className="flex justify-between border-t border-gray-200 pt-2">
                <span className="text-gray-700">Portfolio Value</span>
                <span><V id="portfolio80_20.year10" /></span>
              </div>
              <div className="flex justify-between text-gray-500">
                <span>− Capital Gains Tax</span>
                <span><V id="sellCapitalGainsTax" /></span>
              </div>
              <div className="flex justify-between border-t border-gray-300 pt-2 font-bold">
                <span className="text-gray-900">Net Liquidation</span>
                <span><V id="sellNetLiquidation" /></span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gray-100 border border-gray-300 p-4">
          <p className="text-lg text-gray-900">
            <strong>Bottom Line:</strong> The rent scenario comes out{' '}
            <V id="rentAdvantage" />{' '}
            {/* Note: rentAdvantage can be negative */}
            ahead of selling after 10 years.
          </p>
        </div>
      </section>

      {/* Return Assumptions */}
      <section id="returns" className="mb-8 scroll-mt-16">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Return Assumptions</h2>
        <p className="text-gray-700 mb-4">
          We assume <V id="stockReturns" format="percent" decimals={1} /> real stock returns
          and <V id="bondReturns" format="percent" decimals={1} /> real bond returns.
          After accounting for dividend/interest tax drag:
        </p>
        <ul className="list-disc list-inside text-gray-700 space-y-1 mb-4">
          <li>Effective stock return: <V id="effectiveStockReturn" format="percent" decimals={2} /></li>
          <li>Effective bond return: <V id="effectiveBondReturn" format="percent" decimals={2} /></li>
          <li>Blended 80/20 return: <V id="blendedReturn" format="percent" decimals={2} /></li>
        </ul>
      </section>

      {/* Tax Constants */}
      <section id="tax-constants" className="mb-8 scroll-mt-16">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Tax Constants (Sources)</h2>
        <p className="text-gray-600 mb-4 text-sm">
          These are source values with citations—click to see where they come from.
        </p>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Fed. Depreciation Recapture</span>
            <span><V id="federalDepreciationRecaptureRate" format="percent" decimals={0} /></span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">CA State Tax Rate</span>
            <span><V id="caStateTaxRate" format="percent" decimals={1} /></span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Prop 13 Max Growth</span>
            <span><V id="prop13MaxGrowth" format="percent" decimals={0} /></span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">S&P 500 Dividend Yield</span>
            <span><V id="sp500DividendYield" format="percent" decimals={1} /></span>
          </div>
        </div>
      </section>

      <div className="border-t border-gray-200 pt-4">
        <ResetButton />
      </div>
    </div>
  );
}

/**
 * Main demo component with provider
 */
export function GraphDemo() {
  return (
    <GraphProvider definition={houseDecisionGraph} persistKey="graph-demo">
      <div className="min-h-screen bg-white pb-16">
        {/* Main content - centered with max width */}
        <div className="max-w-4xl mx-auto px-6 py-8 mr-80">
          <DemoContent />
        </div>

        {/* Inspector sidebar - offset from top for header */}
        <InspectorPanel position="sidebar" width={400} />

        {/* Override banner - fixed at bottom */}
        <OverrideBanner />
      </div>
    </GraphProvider>
  );
}

export default GraphDemo;
